import { useMemo, useState } from 'react';

import { useMutation, useQuery } from '@tanstack/react-query';
import { Link, useParams } from 'react-router';
import { LuCalendarDays, LuClock, LuMapPin } from 'react-icons/lu';

import { ApiError } from '@mds/shared/lib/api';
import { clockRange } from '@mds/shared/lib/format';
import type { GuestEventFeedbackForm } from '@mds/shared/types/event-feedback';
import { Field } from '@mds/shared/ui/form/field';
import { Form, applyServerErrors, useZodForm } from '@mds/shared/ui/form/form';
import { RatingScale } from '@mds/shared/ui/form/rating-scale';
import { Button } from '@mds/shared/ui/shadcn/button';
import { Textarea } from '@mds/shared/ui/shadcn/textarea';

import FormQuestions from '@/components/form/form-questions';
import PageHeader from '@/components/heading/page-header';
import OutcomePanel from '@/components/section/outcome-panel';
import { ErrorBlock, LoadingBlock } from '@/components/states';

import { submitFeedback } from '@/features/feedback/feedback.api';
import { feedbackQueries } from '@/features/feedback/feedback.queries';
import { feedbackDefaults, feedbackSchema } from '@/features/feedback/feedback.schema';
import { formatEventDate } from '@/lib/format';

const band = 'mx-auto max-w-page px-gutter pt-section';
/**
 * The form's column, centred on the page with its header. `box-content` keeps
 * the gutter outside the cap, so the column is the same width at every size.
 */
const column = 'mx-auto box-content max-w-3xl px-gutter pt-section';

type GuestEvent = GuestEventFeedbackForm['event'];

/** The rows of the event card on the closing screens: when and where. */
const eventDetails = (event: GuestEvent) => ({
  title: event.title,
  items: [
    { icon: LuCalendarDays, marker: 'Date', description: formatEventDate(event.eventDate) },
    { icon: LuClock, marker: 'Time', description: clockRange(event.startMinute, event.endMinute) },
    { icon: LuMapPin, marker: 'Space', description: event.space },
  ],
});

const backToEvents = (
  <Button asChild variant="outline" size="lg">
    <Link to="/events">See upcoming events</Link>
  </Button>
);

/**
 * The page behind the link in the post-event feedback email.
 *
 * The token in the URL is the guest's only key — there is no sign-in — so the
 * page has four ends: a link that does not open anything, a form already sent,
 * the form itself, and the thanks once it is sent.
 */
export default function EventFeedbackPage() {
  const { token = '' } = useParams();
  const resolved = useQuery(feedbackQueries.form(token));
  /** Set once this visit has sent the form, or the server says an earlier one did. */
  const [sent, setSent] = useState<'now' | 'before' | null>(null);

  // Every bad link is the same 404, by design: an unknown token, a cancelled
  // registration and a guest who never checked in all look alike to the server.
  if (resolved.isError && resolved.error instanceof ApiError && resolved.error.status === 404) {
    return (
      <div className={band}>
        <OutcomePanel
          tone="neutral"
          title="This feedback link doesn't open a form"
          description="It may be cut short or mistyped. Open it again from your email, or copy the whole link into your browser."
          actions={backToEvents}
        />
      </div>
    );
  }

  if (resolved.isPending || resolved.isError) {
    return (
      <section className={band}>
        {resolved.isPending ? (
          <LoadingBlock />
        ) : (
          <ErrorBlock
            title="We couldn't load your feedback form"
            error={resolved.error}
            onRetry={() => void resolved.refetch()}
          />
        )}
      </section>
    );
  }

  const form = resolved.data;

  if (sent || form.alreadySubmitted) {
    const now = sent === 'now';
    return (
      <div className={band}>
        <OutcomePanel
          tone="success"
          title={now ? 'Thank you for your feedback' : 'You already sent your feedback'}
          description={
            now
              ? 'It helps us plan what we run next at MDS.'
              : `We have your answers for ${form.event.title}. Each link takes one response.`
          }
          details={eventDetails(form.event)}
          actions={backToEvents}
        />
      </div>
    );
  }

  return (
    <section className={column}>
      <PageHeader
        title={`How was ${form.event.title}?`}
        description="Your answers are anonymous. They help us plan what we run next."
        meta={[
          formatEventDate(form.event.eventDate),
          clockRange(form.event.startMinute, form.event.endMinute),
          form.event.space,
        ]}
      />
      <FeedbackForm
        token={token}
        form={form}
        onSent={() => setSent('now')}
        onAlreadySent={() => setSent('before')}
      />
    </section>
  );
}

function FeedbackForm({
  token,
  form,
  onSent,
  onAlreadySent,
}: {
  token: string;
  form: GuestEventFeedbackForm;
  onSent: () => void;
  onAlreadySent: () => void;
}) {
  const schema = useMemo(() => feedbackSchema(form), [form]);
  const fields = useZodForm(schema, { defaultValues: feedbackDefaults(form) });
  /** A failure no field can show. `null` once dismissed or a new send starts. */
  const [formError, setFormError] = useState<unknown>(null);

  const sending = useMutation({
    mutationFn: (body: Parameters<typeof submitFeedback>[1]) => submitFeedback(token, body),
    onMutate: () => setFormError(null),
    onSuccess: onSent,
    onError: (error) => {
      // Sent from another tab, or a retry after a lost response: the answers
      // are in, which is what the guest wanted.
      if (error instanceof ApiError && error.code === 'EVENT_FEEDBACK_ALREADY_SUBMITTED') {
        return onAlreadySent();
      }
      if (applyServerErrors(error, fields.setError)) return;
      // The per-question refusals name the question in their message
      // ("“How did you hear…” is required"), so they read fine on their own.
      setFormError(error);
    },
  });

  return (
    <Form
      form={fields}
      className="mt-block flex flex-col gap-card"
      onSubmit={(body) => sending.mutate(body)}
    >
      {/* The rating opens the first card, with no title: the page heading
          already asks how the event was. The event's own questions follow,
          the ungrouped ones in the same card and each section in its own. */}
      <FormQuestions form={form}>
        <Field
          name="overallRating"
          label="Overall, how would you rate this event?"
          hint="1 is poor, 5 is excellent."
          required
        >
          <RatingScale aria-label="Overall, how would you rate this event?" />
        </Field>

        <Field name="comment" label="Anything you'd like to tell us?">
          <Textarea rows={4} maxLength={5000} />
        </Field>
      </FormQuestions>

      {formError !== null && (
        <ErrorBlock
          title="We couldn't send your feedback"
          error={formError}
          onDismiss={() => setFormError(null)}
        />
      )}

      <div className="flex justify-center">
        <Button type="submit" size="lg" className="min-w-56" disabled={sending.isPending}>
          {sending.isPending ? 'Sending…' : 'Send feedback'}
        </Button>
      </div>
    </Form>
  );
}
